"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";

const navSections = [
  {
    label: "Audience",
    items: [
      { href: "/dashboard/contacts", label: "Contacts" },
      { href: "/dashboard/forms", label: "Forms" },
      { href: "/dashboard/inbox", label: "Inbox" },
    ],
  },
  {
    label: "Email",
    items: [
      { href: "/dashboard/templates", label: "Templates" },
      { href: "/dashboard/sequences", label: "Sequences" },
      { href: "/dashboard/campaigns", label: "Campaigns" },
      { href: "/dashboard/holidays", label: "Holidays" },
    ],
  },
  {
    label: "Monitoring",
    items: [
      { href: "/dashboard/analytics", label: "Analytics" },
      { href: "/dashboard/deliverability", label: "Deliverability" },
      { href: "/dashboard/logs", label: "Logs" },
      { href: "/dashboard/reports", label: "Reports" },
    ],
  },
  {
    label: "Setup",
    items: [
      { href: "/dashboard/brands", label: "Brands" },
      { href: "/dashboard/domains", label: "Domains" },
      { href: "/dashboard/sending-configs", label: "Sending Configs" },
      { href: "/dashboard/api-keys", label: "API Keys" },
    ],
  },
  {
    label: "Account",
    items: [
      { href: "/dashboard/billing", label: "Billing" },
      { href: "/dashboard/team", label: "Team" },
      { href: "/dashboard/settings", label: "Settings" },
    ],
  },
];

const verificationNavSections = [
  {
    label: "Verification",
    items: [
      { href: "/dashboard/verification", label: "Verify Email" },
      { href: "/dashboard/api-keys", label: "API Keys" },
    ],
  },
  {
    label: "Account",
    items: [
      { href: "/dashboard/billing", label: "Billing" },
      { href: "/dashboard/settings", label: "Settings" },
    ],
  },
];

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const [checked, setChecked] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [emailSendingEnabled, setEmailSendingEnabled] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pinned, setPinned] = useState(true);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  useEffect(() => {
    fetch("/api/v1/auth/me")
      .then(async (res) => {
        if (!res.ok) {
          router.replace("/login");
          return;
        }
        const json = (await res.json()) as {
          data?: {
            user?: { email: string };
            org?: { emailSendingEnabled: boolean };
          };
        };
        if (json?.data?.user) {
          setUserEmail(json.data.user.email);
          setEmailSendingEnabled(json.data.org?.emailSendingEnabled ?? true);
          setChecked(true);
        }
      })
      .catch(() => {
        router.replace("/login");
      });
  }, [router]);

  async function handleLogout() {
    await fetch("/api/v1/auth/logout", { method: "POST" });
    router.replace("/login");
  }

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <p className="text-sm text-zinc-400">Loading...</p>
      </div>
    );
  }

  const sidebarContent = (
    <>
      <nav aria-label="Dashboard navigation" className="flex flex-col overflow-y-auto p-2">
        <Link
          href="/dashboard"
          aria-current={pathname === "/dashboard" ? "page" : undefined}
          className={`rounded-md px-3 py-2 text-sm ${
            pathname === "/dashboard"
              ? "bg-zinc-100 font-medium text-zinc-900 dark:bg-zinc-800 dark:text-white"
              : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-white"
          }`}
        >
          Overview
        </Link>
        {(emailSendingEnabled ? navSections : verificationNavSections).map((section) => (
          <div key={section.label} className="mt-5 border-t border-zinc-200 pt-4 dark:border-zinc-800">
            <div className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 dark:text-zinc-600">
              {section.label}
            </div>
            <div className="flex flex-col gap-0.5">
              {section.items.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={pathname === item.href || pathname.startsWith(item.href + "/") ? "page" : undefined}
                  className={`rounded-md px-3 py-1.5 text-sm ${
                    pathname === item.href || pathname.startsWith(item.href + "/")
                      ? "bg-zinc-100 font-medium text-zinc-900 dark:bg-zinc-800 dark:text-white"
                      : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-white"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="mt-auto border-t border-zinc-200 p-2 dark:border-zinc-800">
        {userEmail.endsWith("@inventivehq.com") && (
          <Link
            href="/dashboard/admin"
            className={`block rounded-md px-3 py-2 text-sm ${
              pathname === "/dashboard/admin"
                ? "bg-zinc-100 font-medium text-zinc-900 dark:bg-zinc-800 dark:text-white"
                : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-white"
            }`}
          >
            Admin
          </Link>
        )}
        <Link
          href="/docs"
          className="block rounded-md px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-white"
        >
          Docs
        </Link>
        <div className="px-3 py-2 text-xs text-zinc-400 truncate">{userEmail}</div>
        <button
          onClick={handleLogout}
          className="w-full rounded-md px-3 py-2 text-left text-sm text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-white"
        >
          Log out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-zinc-50 font-sans dark:bg-zinc-950">
      {/* Pinned sidebar — desktop only */}
      {pinned && (
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-zinc-200 bg-white md:flex dark:border-zinc-800 dark:bg-zinc-950">
          <div className="flex h-14 items-center justify-between border-b border-zinc-200 px-4 dark:border-zinc-800">
            <Link href="/" className="flex items-center gap-2 text-lg font-bold tracking-tight text-zinc-900 dark:text-white">
              <img src="/logo.png" alt="PushMail" className="h-6 w-6" />
              PushMail
            </Link>
            <button
              onClick={() => setPinned(false)}
              className="rounded-md p-1 text-zinc-400 hover:text-zinc-900 dark:hover:text-white"
              aria-label="Unpin sidebar"
              title="Unpin sidebar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 17v5" /><path d="M9 2h6l-1 7h4l-2 4H8l-2-4h4L9 2z" /></svg>
            </button>
          </div>
          {sidebarContent}
        </aside>
      )}

      {/* Top header bar */}
      <div className={`fixed inset-x-0 top-0 z-40 flex h-14 items-center gap-3 border-b border-zinc-200 bg-white px-4 dark:border-zinc-800 dark:bg-zinc-950 ${pinned ? "md:pl-64" : ""}`}>
        <button
          onClick={() => {
            // On desktop: toggle pin. On mobile: open drawer.
            if (window.innerWidth >= 768) {
              setPinned((p) => !p);
            } else {
              setSidebarOpen(true);
            }
          }}
          className={`rounded-md p-1 text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white ${pinned ? "md:hidden" : ""}`}
          aria-label="Open menu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <Link href="/" className={`flex items-center gap-2 text-lg font-bold tracking-tight text-zinc-900 dark:text-white ${pinned ? "md:hidden" : ""}`}>
          <img src="/logo.png" alt="PushMail" className="h-6 w-6" />
          PushMail
        </Link>
        <div className="ml-auto">
          <Link
            href={emailSendingEnabled ? "/dashboard/brands/new" : "/dashboard/verification"}
            className="flex items-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
          >
            {emailSendingEnabled && <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>}
            {emailSendingEnabled ? "Add Brand" : "Verify Email"}
          </Link>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => { if (e.key === "Escape") setSidebarOpen(false); }}
        >
          <aside
            className="flex h-full w-64 flex-col border-r border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-14 items-center justify-between border-b border-zinc-200 px-4 dark:border-zinc-800">
              <Link href="/" className="flex items-center gap-2 text-lg font-bold tracking-tight text-zinc-900 dark:text-white">
                <img src="/logo.png" alt="PushMail" className="h-6 w-6" />
                PushMail
              </Link>
              <button
                onClick={() => setSidebarOpen(false)}
                className="rounded-md p-1 text-zinc-500 hover:text-zinc-900 dark:hover:text-white"
                aria-label="Close menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main content */}
      <main id="main-content" className={`flex-1 overflow-auto pt-14 ${pinned ? "md:ml-64" : ""}`}>
        <div className="mx-auto max-w-5xl px-6 py-8">
          {children}
        </div>
      </main>
    </div>
  );
}
